// GtSequenceStart (C1-SEQ2) — "Start sequence" for the people ticked on a role
// pipeline, a bench or the candidate list.
//
// Pick the role (when not already on one), the plan, the sending address and
// whether the company may be named. Everyone is checked first: warnings are
// grouped by reason, nobody is quietly dropped, and people can be left out by
// reason in one click. Start writes every step for every person on the server
// and opens the run, where nothing sends until each person is approved.
//
// Props: candidateIds, roleId (optional, id or ref), roleTitle (optional),
//        source { kind, id, label }, authFetch, onClose, navigate
import { useState, useEffect, useMemo, useRef } from 'react';
import { GtSelect, GtSearchSelect } from './GtControls';

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);
export function planSummary(p) {
  if (!p) return '';
  const steps = Array.isArray(p.steps) ? p.steps : [];
  const last = steps.length ? steps[steps.length - 1].day : 0;
  return plural(steps.length, 'email', 'emails') + (steps.length > 1 ? ' over ' + plural(last, 'day', 'days') : '')
    + (p.linkedin_on ? ', and ' + plural(p.linkedin_messages, 'LinkedIn message', 'LinkedIn messages') : ''); // C1-SEQ7: no connection note
}

export default function GtSequenceStart({ candidateIds, roleId, roleTitle, source, authFetch, onClose, navigate }) {
  const [roles, setRoles] = useState(null);
  const [role, setRole] = useState(roleId ? String(roleId) : '');
  const [plans, setPlans] = useState(null);
  const [planId, setPlanId] = useState('');
  const [senders, setSenders] = useState(null);
  const [senderId, setSenderId] = useState('');
  const [disclose, setDisclose] = useState(false);
  const [guidance, setGuidance] = useState('');
  const [booking, setBooking] = useState(''); // C1-SEQ7: calendar booking link
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [left, setLeft] = useState({}); // id -> true when left out
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showReason, setShowReason] = useState(null);
  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const seq = useRef(0);
  const n = candidateIds.length;

  useEffect(() => {
    authFetch('/api/sequences/plans').then((r) => {
      setPlans(r.plans || []);
      const d = (r.plans || []).find((p) => p.is_default) || (r.plans || [])[0];
      if (d) setPlanId(String(d.id));
    }).catch((e) => { setPlans([]); setError(e.message); });
    authFetch('/api/sequences/booking-default').then((r) => { if (r && r.booking_url) setBooking(r.booking_url); }).catch(() => null); // C1-SEQ7
    authFetch('/api/email/senders').then((r) => {
      setSenders(r.senders || []);
      const d = (r.senders || []).find((s) => s.is_default) || (r.senders || [])[0];
      if (d) setSenderId(String(d.id));
    }).catch(() => setSenders([]));
    if (!roleId) {
      authFetch('/api/roles').then((r) => {
        const list = (Array.isArray(r) ? r : (r.roles || [])).filter((x) => ['filled', 'closed'].indexOf(x.status) === -1);
        list.sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setRoles(list);
      }).catch(() => setRoles([]));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // Check everyone as soon as the role and plan are known.
  useEffect(() => {
    if (!role || !planId) { setCheck(null); return; }
    const mine = ++seq.current;
    setChecking(true); setError(null);
    authFetch('/api/sequences/check', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ role_id: role, plan_id: planId, candidate_ids: candidateIds }) })
      .then((r) => { if (mine === seq.current) { setCheck(r); setLeft({}); } })
      .catch((e) => { if (mine === seq.current) setError(e.message); })
      .finally(() => { if (mine === seq.current) setChecking(false); });
  }, [role, planId]); // eslint-disable-line react-hooks/exhaustive-deps

  const plan = (plans || []).find((p) => String(p.id) === String(planId)) || null;
  const sender = (senders || []).find((s) => String(s.id) === String(senderId)) || null;
  const people = useMemo(() => (check ? check.people : []), [check]);
  const already = people.filter((p) => p.already_in);
  const eligible = people.filter((p) => !p.already_in);
  const going = eligible.filter((p) => !left[p.id]);
  const reasons = useMemo(() => {
    const m = new Map();
    people.filter((p) => !p.already_in).forEach((p) => p.warnings.forEach((w) => {
      const r = m.get(w.key) || { key: w.key, label: String(w.text).split(':')[0].replace(/ \(.*\)$/, ''), ids: [] };
      r.ids.push(p.id); m.set(w.key, r);
    }));
    return Array.from(m.values()).sort((a, b) => b.ids.length - a.ids.length);
  }, [people]);
  const setMany = (ids, out) => setLeft((l) => { const x = { ...l }; ids.forEach((id) => { if (out) x[id] = true; else delete x[id]; }); return x; });
  const listBase = showReason ? eligible.filter((p) => p.warnings.some((w) => w.key === showReason)) : people;
  const ql = query.trim().toLowerCase();
  const listShown = ql ? listBase.filter((p) => (p.name + ' ' + (p.email || '')).toLowerCase().indexOf(ql) !== -1) : listBase;
  const roleOptions = useMemo(() => (roles || []).map((r) => ({ value: r.id, label: r.title + (r.client_name ? ' · ' + r.client_name : '') })), [roles]);
  const roleName = roleTitle || ((roles || []).find((r) => String(r.id) === String(role)) || {}).title || '';
  const cost = Math.max(0.01, going.length * 0.05);

  async function start() {
    if (!going.length) return;
    setBusy(true); setError(null);
    try {
      const r = await authFetch('/api/sequences/runs', {
        method: 'POST', headers: JSON_HEADERS,
        body: JSON.stringify({
          role_id: role, plan_id: planId, sender_id: senderId || null, candidate_ids: going.map((p) => p.id),
          disclose_client: disclose, instructions: guidance.trim() || null, booking_url: booking.trim() || null, // C1-SEQ7
          source_kind: source && source.kind ? source.kind : 'role', source_id: source && source.id ? source.id : null,
          source_label: source && source.label ? source.label : null,
        }),
      });
      onClose();
      if (navigate) navigate('/sequences/' + r.run.id);
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtsf-modal gtsq-start" role="dialog" aria-modal="true" aria-labelledby="gtsq-start-title" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head gtsf-head" id="gtsq-start-title">
          <span>Start a sequence</span>
          <span className="gtsf-to">{plural(n, 'person', 'people')}{source && source.label ? ' · ' + source.label : ''}</span>
        </div>
        <div className="gtc-modal-body gtsf-body">
          {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}

          <div className="gtsq-form">
            <div className="gtc-field"><span>Role</span>
              {roleId ? <div className="gtsq-fixed">{roleName || 'This role'}</div>
                : roles === null ? <div className="gtsf-muted">Loading roles…</div>
                  : <GtSearchSelect value={role} onChange={(v) => setRole(String(v))} options={roleOptions} placeholder="Which role is this about?" searchPlaceholder={'Search ' + roleOptions.length + ' open roles'} />}
            </div>
            <div className="gtc-field"><span>Plan</span>
              {plans === null ? <div className="gtsf-muted">Loading…</div>
                : <GtSelect value={planId} onChange={(v) => setPlanId(String(v))} placeholder="Choose a plan" options={plans.map((p) => ({ value: p.id, label: p.name }))} />}
              {plan && (
                <div className="gtsq-plan">
                  <div className="gtsq-plan-sum">{planSummary(plan)}</div>
                  <ol className="gtsq-plan-steps">
                    {(plan.steps || []).map((s, i) => <li key={i}><b>Day {s.day}</b><span>{s.purpose}</span></li>)}
                  </ol>
                </div>
              )}
            </div>
            <div className="gtc-field"><span>From</span>
              {senders === null ? <div className="gtsf-muted">Loading…</div>
                : senders.length === 0 ? <div className="gtnt-banner gtnt-banner--amber">Add a sending address first: open Email on anyone and choose Manage addresses.</div>
                  : <GtSelect value={senderId} onChange={(v) => setSenderId(String(v))} options={senders.map((s) => ({ value: s.id, label: s.display_name + ' <' + s.email + '>' }))} />}
              {sender && !sender.signature_html && <div className="gtsf-hint">This address has no signature, so the emails end without a name. Add one under Manage addresses in the email box.</div>}
            </div>
            <label className="gtc-field"><span>Calendar booking link <em className="gtbn-opt">optional</em></span>{/* C1-SEQ7 */}
              <input type="url" className="gtsq-booking" value={booking} maxLength={500} placeholder="https://calendly.com/your-name/20min" onChange={(e) => setBooking(e.target.value)} />
              {booking.trim() && !/^https:\/\/\S{4,}$/i.test(booking.trim())
                ? <span className="gtsq-warntext gtsq-bookhint">Use the full address, starting with https://</span>
                : <span className="gtsf-hint">Email 1, the last email, and the first and last LinkedIn messages end with a line leading to it. Left empty, they ask for a reply instead.</span>}
            </label>
            <label className="gtem-check gtsq-disclose">
              <input type="checkbox" checked={disclose} onChange={(e) => setDisclose(e.target.checked)} />
              <span>Name the hiring company in these messages. Leave this off for a confidential search: the company is then described, never named, and anything that names it is flagged.</span>
            </label>
            <label className="gtc-field"><span>Anything the writer should know <em className="gtbn-opt">optional</em></span>
              <textarea className="gtsq-guidance" rows={2} maxLength={1500} value={guidance} onChange={(e) => setGuidance(e.target.value)}
                placeholder="For example: hybrid, two days a week in the office. Mention the new data platform." />
            </label>
          </div>

          {(checking || check) && (
            <div className="gtsq-check">
              {checking && !check ? <div className="gtsf-muted">Checking {plural(n, 'person', 'people')}…</div> : check && (
                <>
                  <div className="gtsf-tiles">
                    <div className="gtsf-tile gtsf-tile--go"><b>{going.length}</b><span>will be written for</span></div>
                    <div className={'gtsf-tile' + (reasons.length ? ' gtsf-tile--warn' : '')}><b>{eligible.filter((p) => p.warnings.length).length}</b><span>with something to know</span></div>
                    <div className="gtsf-tile"><b>{already.length}</b><span>already in a sequence for this role</span></div>
                  </div>
                  {reasons.length > 0 && (
                    <div className="gtem-reasons">
                      {reasons.map((r) => {
                        const allOut = r.ids.every((id) => left[id]);
                        return (
                          <div key={r.key} className={'gtem-reason' + (showReason === r.key ? ' on' : '')}>
                            <b>{r.ids.length}</b><span>{r.label}</span>
                            <button type="button" className="gtsf-linkbtn" onClick={() => setShowReason(showReason === r.key ? null : r.key)}>{showReason === r.key ? 'Show everyone' : 'Show'}</button>
                            <button type="button" className="gtsf-linkbtn" onClick={() => setMany(r.ids, !allOut)}>{allOut ? 'Put them back' : 'Leave these out'}</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(showReason || people.length <= 50 || listOpen) ? (
                    <>
                      {people.length > 8 && <input className="gtem-psearch" type="search" placeholder={'Find someone in these ' + people.length} value={query} onChange={(e) => setQuery(e.target.value)} />}
                      <div className="gtem-plist gtsq-plist">
                        {listShown.slice(0, 200).map((p) => (
                          <div key={p.id} className={'gtem-prow' + (p.already_in || left[p.id] ? ' gtem-prow--off' : '') + (p.warnings.length ? ' gtem-prow--warn' : '')}>
                            <input type="checkbox" aria-label={'Include ' + p.name} disabled={p.already_in} checked={!p.already_in && !left[p.id]} onChange={(e) => setMany([p.id], !e.target.checked)} />
                            <div className="gtem-pmain">
                              <div className="gtem-pname"><b>{p.name}</b>{p.email ? <span>{p.email}</span> : null}</div>
                              {p.already_in && <div className="gtem-warns"><span className="gtem-warn">Already in a sequence for this role, so left out</span></div>}
                              {!p.already_in && p.warnings.length > 0 && <div className="gtem-warns">{p.warnings.map((w) => <span key={w.key} className={'gtem-warn gtem-warn--' + w.key}>{w.text}</span>)}</div>}
                            </div>
                          </div>
                        ))}
                        {listShown.length > 200 && <div className="gtsf-muted gtem-pempty">Showing 200 of {listShown.length}. Search to find someone.</div>}
                      </div>
                    </>
                  ) : (
                    <button type="button" className="gtem-seeall" onClick={() => setListOpen(true)}>See all {people.length} people</button>
                  )}
                  <div className="gtsf-muted gtsq-cost">
                    Each person's emails{plan && plan.linkedin_on ? ' and LinkedIn messages' : ''} are written for them, about {'£'}{cost.toFixed(2)} in all.
                    Nothing is sent until you approve each person on the next screen.
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="gtc-modal-foot">
          <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="gtc-primary" disabled={busy || checking || !check || !going.length || !sender || (booking.trim() && !/^https:\/\/\S{4,}$/i.test(booking.trim()))} onClick={start}>
            {busy ? 'Starting…' : !role ? 'Pick a role first' : !planId ? 'Pick a plan first' : (checking || !check) ? 'Checking…'
              : 'Write for ' + plural(going.length, 'person', 'people')}{/* C1-SEQ2B */}
          </button>
        </div>
      </div>
    </div>
  );
}
